import {
  BorderTopWidth,
  BorderTopStyle,
  BorderBottomColor,
  Color,
} from '@wix/css-property-parser';
import type { AggregatedStyleValues } from '../../common/abstractStyles/types';
import {
  COMBINED_BORDER_PROPERTIES,
  getBorderProperty,
} from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/border/constants';
import type { AtomicValueWithNesting } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';
import { decompose } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';
import { VariableResolver } from '../../common/abstractStyles/variableResolver';
import { ComponentsPlatformService } from '@wix/editor-platform-manifest-services';

/**
 * Border value type that accepts parsed values from css-property-parser
 */
export type BorderValue = {
  type: 'Border' | 'border-individual';
  width?: ReturnType<typeof BorderTopWidth.parse>;
  style?: ReturnType<typeof BorderTopStyle.parse>;
  color?: ReturnType<typeof Color.parse>;
};

/**
 * Border control props expected by the dumb Border component
 */
export type BorderControlProps = {
  value: BorderValue;
  shouldPassPropertyType: boolean;
  onChange: (fullValue: unknown, partials?: unknown) => void;
  disabled: boolean;
  borderWidthProps?: { disabled?: boolean; isMixed?: boolean };
  borderStyleProps?: { disabled?: boolean; isMixed?: boolean };
  borderColorProps?: { disabled?: boolean; isMixed?: boolean };
};

/**
 * Border change event from the dumb Border component
 */
export type BorderChangeEvent = {
  fullValue: string; // css border value
};

export type AGGREGATED_BORDER_VALUE = {
  value: string | null;
  resolvedValue: string | null;
  uniqueResolvedValues: string[];
  isMixed: boolean;
  isEligible: boolean;
};

/**
 * Get aggregated border value from decomposed atomics.
 *
 * Logic:
 * - Check if property is eligible (exists in decomposed, even with empty array)
 * - If all sides have the SAME value → return that value, isMixed: false, isEligible: true
 * - If sides have DIFFERENT values → return 'any', isMixed: true, isEligible: true
 * - If NO values BUT eligible → return null, isMixed: false, isEligible: true
 * - If NOT eligible → return null, isMixed: false, isEligible: false
 *
 * @param values - AggregatedStyleValues from useGlobalDesignStyles
 * @param property - Border sub-property: Width, Style, or Color
 * @returns Object with unified value (or null if mixed/empty), isMixed flag, and eligibility
 */
export function getAggregatedBorderValue(
  values: AggregatedStyleValues,
  property: 'Width' | 'Style' | 'Color',
): AGGREGATED_BORDER_VALUE {
  const atomicValues: AtomicValueWithNesting[] = [];
  let hasAnyEligibleProperty = false;

  // Collect all atomic values from all border sides
  COMBINED_BORDER_PROPERTIES.forEach((direction) => {
    const atomicProperty = getBorderProperty(direction, property);

    // Source of truth: values.decomposed
    // Check cssProperties namespace first
    if (
      values.mode !== 'cssCustomProperties' &&
      values.decomposed.cssProperties[atomicProperty] !== undefined
    ) {
      hasAnyEligibleProperty = true; // Property exists in decomposed (eligible)
      atomicValues.push(...values.decomposed.cssProperties[atomicProperty]);
    }

    // Fallback to cssCustomProperties namespace
    if (
      values.mode !== 'cssProperties' &&
      values.decomposed.cssCustomProperties[atomicProperty] !== undefined
    ) {
      hasAnyEligibleProperty = true; // Property exists in decomposed (eligible)
      atomicValues.push(
        ...values.decomposed.cssCustomProperties[atomicProperty],
      );
    }
  });

  const uniqueValues = new Set(
    atomicValues.map((value) => value.resolvedValue),
  );
  const uniqueResolvedValues = Array.from(uniqueValues);

  // Not eligible - property not in manifest
  if (!hasAnyEligibleProperty) {
    return {
      value: null,
      resolvedValue: null,
      uniqueResolvedValues: [],
      isMixed: false,
      isEligible: false,
    };
  }

  // Eligible but no values - property in manifest but no current value
  if (uniqueValues.size === 0) {
    return {
      value: null,
      resolvedValue: null,
      uniqueResolvedValues,
      isMixed: false,
      isEligible: true,
    };
  }

  // Single value - all sides have same value
  if (uniqueValues.size === 1) {
    return {
      value: atomicValues[0].value,
      resolvedValue: atomicValues[0].resolvedValue,
      uniqueResolvedValues,
      isMixed: false,
      isEligible: true,
    };
  }

  // Multiple values - mixed state
  return {
    value: 'Mixed',
    resolvedValue: 'Mixed',
    uniqueResolvedValues,
    isMixed: true,
    isEligible: true,
  };
}

/**
 * Props mapper: AggregatedStyleValues → Border control props
 *
 * Reads from values.decomposed (source of truth for atomics).
 * Returns Border control props with isMixed/disabled flags.
 */
export const mapBorderProps = (
  values: AggregatedStyleValues,
): Omit<BorderControlProps, 'onChange'> => {
  // Get aggregated values for each border sub-property
  const width = getAggregatedBorderValue(values, 'Width');
  const style = getAggregatedBorderValue(values, 'Style');
  const color = getAggregatedBorderValue(values, 'Color');

  // Build Border value object
  const borderValue: BorderValue = {
    type: 'Border',
  };

  // Parse width
  if (width.value) {
    const parsed = BorderTopWidth.parse(width.value);
    if (parsed) {
      borderValue.width = parsed;
    }
  }

  // Parse style
  if (style.value) {
    const parsed = BorderTopStyle.parse(style.value);
    if (parsed) {
      borderValue.style = parsed;
    }
  }

  // Parse color
  if (color.value) {
    const parsed = Color.parse(color.value);
    if (parsed) {
      borderValue.color = parsed;
    }
  }

  // Build control-specific props
  const buildSubControlProps = (
    isEligible: boolean,
    isMixed: boolean,
  ): { disabled?: boolean; isMixed?: boolean } | undefined => {
    const props: { disabled?: boolean; isMixed?: boolean } = {};

    if (!isEligible) {
      props.disabled = true; // Not eligible - property not in manifest
    }

    if (isMixed) {
      props.isMixed = true; // Multiple values - show mixed state
    }

    return Object.keys(props).length > 0 ? props : undefined;
  };

  return {
    value: borderValue,
    shouldPassPropertyType: true,
    borderWidthProps: buildSubControlProps(width.isEligible, width.isMixed),
    borderStyleProps: buildSubControlProps(style.isEligible, style.isMixed),
    borderColorProps: buildSubControlProps(color.isEligible, color.isMixed),
    disabled: false,
  };
};

/**
 * Event mapper: Border control onChange → CSS property changes
 *
 * Handles CSS variables in border values:
 * 1. Extract variable names from original value (e.g., "var(--border-color)")
 * 2. Resolve variables to actual values so CSS parser can work
 * 3. Use decompose() to parse the resolved border string
 * 4. Restore original variable names in the parsed properties
 * 5. Return CSS shorthand changes (borderWidth, borderStyle, borderColor)
 *
 * Example flow:
 * - Input: "1px solid var(--border-color)"
 * - Resolved: "1px solid #ff0000" (for parsing)
 * - Parsed: { borderTopWidth: "1px", borderTopStyle: "solid", borderTopColor: "#ff0000", ... }
 * - Restored: { borderWidth: "1px", borderStyle: "solid", borderColor: "var(--border-color)" }
 * - Engine then decomposes shorthands to atomics and applies to all sides
 */
export const mapBorderEvent = (
  event: BorderChangeEvent,
  componentsAPI: ComponentsPlatformService,
): Record<string, string> => {
  // TODO: rewrite once components will return partials for borderWidth, borderStyle, borderColor
  const { fullValue } = event;

  if (!fullValue || typeof fullValue !== 'string') {
    return {};
  }

  // Step 1: Extract CSS variable names from the original value
  // e.g., "1px solid var(--border-color)" → ["border-color"]
  const variableNames = VariableResolver.extractVariableNames(fullValue);

  // Step 2: Get theme variables from componentsAPI
  // Convert variable names to the format expected by the API (without --)
  const themeVariables: Record<string, string> = {};
  const resolvedToVariable: Record<string, string> = {};
  if (variableNames.length > 0) {
    // Get theme variables (keys should have -- prefix for the API)
    const themeProps = componentsAPI.theme.getThemeCustomProperties({
      keys: variableNames.map((name) => `--${name}`),
    });

    // Build themeVariables map for resolveVariables utility
    // and resolvedToVariable map for restoration
    Object.entries(themeProps).forEach(([varName, data]) => {
      if ('value' in data) {
        // Strip -- prefix for resolveVariables utility (expects keys without --)
        const cleanVarName = varName.startsWith('--')
          ? varName.slice(2)
          : varName;
        themeVariables[cleanVarName] = data.value;
        // Map resolved value back to original variable reference
        // Normalize color using same function as parser to match decomposed output
        const parsedColor = Color.parse(data.value);
        const normalizedColor =
          parsedColor && BorderBottomColor.toCSSValue(parsedColor);
        const normalizedKey = normalizedColor
          ? normalizedColor.toLowerCase()
          : data.value.toLowerCase();
        resolvedToVariable[normalizedKey] = `var(--${cleanVarName})`;
      }
    });
  }

  // Step 3: Resolve variables so the CSS parser can work
  // "1px solid var(--border-color)" → "1px solid #ff0000"
  const resolvedValue = VariableResolver.resolveVariables(
    fullValue,
    themeVariables,
  );
  // Step 4: Parse the resolved border value using decompose
  const decomposed = decompose('border', resolvedValue);
  if (!decomposed) {
    return {};
  }

  // Step 5: Detect what changed and expand to ALL atomic per-side properties
  // Return atomic properties (not shorthands) for updateStyles to handle smart propagation
  const changes: Record<string, string> = {};

  // Detect which sub-properties changed by checking decomposed output
  let widthValue: string | null = null;
  let styleValue: string | null = null;
  let colorValue: string | null = null;

  Object.entries(decomposed).forEach(([property, value]) => {
    if (value === null) {
      return;
    }

    // Check if this value was resolved from a variable
    const originalVariable = resolvedToVariable[value.toLowerCase()];
    const finalValue = originalVariable || value;

    // Detect which sub-property this belongs to
    if (property.includes('Width')) {
      widthValue = finalValue;
    } else if (property.includes('Style')) {
      styleValue = finalValue;
    } else if (property.includes('Color')) {
      colorValue = finalValue;
    }
  });

  // Expand changed sub-properties to ALL atomic per-side properties
  // This gives updateStyles all the atomic properties it needs for smart propagation
  if (widthValue !== null) {
    // Width changed - set all atomic width properties
    const value = widthValue; // Capture for closure
    COMBINED_BORDER_PROPERTIES.forEach((direction) => {
      changes[getBorderProperty(direction, 'Width')] = value;
    });
  }

  if (styleValue !== null) {
    // Style changed - set all atomic style properties
    const value = styleValue; // Capture for closure
    COMBINED_BORDER_PROPERTIES.forEach((direction) => {
      changes[getBorderProperty(direction, 'Style')] = value;
    });
  }

  if (colorValue !== null) {
    // Color changed - set all atomic color properties
    const value = colorValue; // Capture for closure
    COMBINED_BORDER_PROPERTIES.forEach((direction) => {
      changes[getBorderProperty(direction, 'Color')] = value;
    });
  }

  return changes;
};
